/**
 * Note list column — the strict middle column of the three-pane layout.
 * Owns its full-height header (view title, count, contextual actions) so no
 * bar ever spans across the list and editor panes.
 */
import { FileText, Lock, Menu, Plus } from 'lucide-react';
import type { NoteRecord } from '../lib/sync/dto.js';
import { notePreview } from '../lib/notes/store.js';
import { useOverlayScrollbar } from '../lib/ui/overlayScroll.js';

interface NoteListProps {
  notes: NoteRecord[];
  /** View title shown in the header: "Notes", a folder name, "Trash", "Vault". */
  headerTitle: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Omitted in the vault view — locked notes are never created there. */
  onCreate?: () => void;
  /** Contextual header actions (Lock, delete-folder…) rendered before the +. */
  actions?: React.ReactNode;
  /** Mobile only: opens the sidebar drawer. Omitted on desktop. */
  onOpenMenu?: () => void;
  emptyLabel: string;
  /** Rendered below the header, e.g. sync/storage banners. */
  banner?: React.ReactNode;
}

function formatRelative(date: Date): string {
  const diff = Date.now() - date.getTime();
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diff < minute) return 'just now';
  if (diff < hour) return `${Math.floor(diff / minute)}m ago`;
  if (diff < day) return `${Math.floor(diff / hour)}h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function NoteList({
  notes,
  headerTitle,
  selectedId,
  onSelect,
  onCreate,
  actions,
  onOpenMenu,
  emptyLabel,
  banner,
}: NoteListProps) {
  // Floating capsule scrollbar (macOS overlay parity) — see lib/ui/overlayScroll.ts.
  const listScroll = useOverlayScrollbar<HTMLDivElement>();
  return (
    <div className="note-list">
      <div className="note-list-header">
        {onOpenMenu && (
          <button
            type="button"
            className="icon-button menu-button"
            onClick={onOpenMenu}
            title="Open sidebar"
            aria-label="Open sidebar"
          >
            <Menu size={18} strokeWidth={1.75} />
          </button>
        )}
        <span className="note-list-header-title" title={headerTitle}>
          {headerTitle}
        </span>
        <div className="note-list-header-actions">
          {actions}
          {onCreate && (
            <button type="button" className="icon-button" onClick={onCreate} title="New note">
              <Plus size={15} strokeWidth={1.75} />
            </button>
          )}
        </div>
      </div>

      {banner}

      <div className="ob-host ob-host-grow" ref={listScroll.hostRef}>
        <div className="note-list-scroll" ref={listScroll.scrollerRef}>
        {notes.length === 0 ? (
          <div className="note-list-empty">{emptyLabel}</div>
        ) : (
          notes.map((note) => {
            const isSelected = note.id === selectedId;
            return (
              <div
                key={note.id}
                className="note-row"
                style={{ background: isSelected ? 'var(--active)' : undefined }}
                onClick={() => onSelect(note.id)}
                role="button"
                aria-current={isSelected}
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(note.id);
                  }
                }}
              >
                <div className="note-row-title">
                  {note.isVaulted && (
                    <Lock size={12} strokeWidth={1.75} className="note-row-icon" />
                  )}
                  {!note.isVaulted && <FileText size={12} strokeWidth={1.75} className="note-row-icon" />}
                  <span className="note-row-title-text">{note.title}</span>
                </div>
                <div className="note-row-preview">{notePreview(note.content)}</div>
                <div className="note-row-meta">
                  {note.trashedFrom ? `From ${note.trashedFrom} · ` : ''}
                  {formatRelative(note.deletedAt ?? note.updatedAt)}
                </div>
              </div>
            );
          })
        )}
        </div>
      </div>
    </div>
  );
}
